import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import {
  TreeSatItem,
  GeoFeature,
  GroundStation,
  Strategy,
  Satellite,
  SatGroup,
  OrbitStatusMeta,
  AccessWindowPair,
  AccessAnalysisReport,
  CoverageAnalysisReport,
} from '../types/mission';
import { STRATEGIES } from '../data/strategies';
import type { SatelliteGroupDataType } from '../../../modelling/types';

type OrbitEventPayload = OrbitStatusMeta & { subType: 'group' | 'satellite' };

// ─── Converter: TreeSatItem[] → SatelliteGroupDataType[] ─────────────────────
//
// drawSatelliteGroupOrbits only reads sat.id, sat.name, sat.orbitColour, and
// sat.orbitParameters fields; config/busComponent are stubs.
function treeItemsToSatGroups(items: TreeSatItem[]): SatelliteGroupDataType[] {
  const EARTH_RADIUS = 6371;

  // Flatten: top-level satellites + children from groups
  const sats: Satellite[] = [];
  for (const item of items) {
    if ((item as SatGroup).type === 'group') {
      for (const child of (item as SatGroup).children) {
        if (child.orbitElements || child.tleMeta) sats.push(child);
      }
    } else {
      const sat = item as Satellite;
      if (sat.orbitElements || sat.tleMeta) sats.push(sat);
    }
  }

  if (sats.length === 0) return [];

  // Reference orbit: first Keplerian sat, or a neutral stub when only TLE sats exist
  const refEl = sats.find(s => s.orbitElements)?.orbitElements;
  const referenceOrbit = refEl
    ? {
        altitude: refEl.semiMajorAxis - EARTH_RADIUS,
        semiMajorAxis: refEl.semiMajorAxis,
        eccentricity: refEl.eccentricity,
        inclination: refEl.inclination,
        RAAN: refEl.RAAN,
        AOP: refEl.AOP,
        TA: refEl.TA,
        epoch: refEl.epoch,
      }
    : {
        altitude: 400,
        semiMajorAxis: EARTH_RADIUS + 400,
        eccentricity: 0,
        inclination: 0,
        RAAN: 0,
        AOP: 0,
        TA: 0,
        epoch: 0,
      };

  return [
    {
      groupId: 'mock-group',
      name: 'Satellites',
      type: 'Constellation',
      isGroupSwathConfigEffective: true,
      launched: true,
      orbitType: 'Custom',
      referenceOrbit,
      satellites: sats.map((sat) => {
        if (sat.tleMeta) {
          return {
            id: sat.id,
            name: sat.name,
            orbitColour: sat.orbitColour,
            active: sat.active !== false,
            config: {} as any,
            busComponent: { thrusters: [] },
            orbitParameters: {
              altitude: 0,
              semiMajorAxis: 0,
              eccentricity: 0,
              inclination: 0,
              RAAN: 0,
              AOP: 0,
              TA: 0,
              epoch: 0,
            },
            tleLines: {
              line1: sat.tleMeta.line1,
              line2: sat.tleMeta.line2,
            },
            sensors: sat.sensors,
            ephemeris: sat.ephemeris,
            backendOrbitTrack: sat.backendOrbitTrack,
            propagationStatus: sat.propagationStatus,
          } as any;
        }
        const el = sat.orbitElements!;
        return {
          id: sat.id,
          name: sat.name,
          orbitColour: sat.orbitColour,
          active: sat.active !== false,
          config: {} as any,
          busComponent: { thrusters: [] },
          orbitParameters: {
            altitude: el.semiMajorAxis - EARTH_RADIUS,
            semiMajorAxis: el.semiMajorAxis,
            eccentricity: el.eccentricity,
            inclination: el.inclination,
            RAAN: el.RAAN,
            AOP: el.AOP,
            TA: el.TA,
            epoch: el.epoch,
          },
          sensors: sat.sensors,
          ephemeris: sat.ephemeris,
          backendOrbitTrack: sat.backendOrbitTrack,
          propagationStatus: sat.propagationStatus,
        };
      }),
    },
  ];
}

// State
interface MissionState {
  satellites: TreeSatItem[];
  orbitStatusByGroup: Record<string, OrbitStatusMeta>;
  orbitStatusBySatellite: Record<string, OrbitStatusMeta>;
  satelliteUpdatesById: Record<string, Partial<Satellite>>;
  geoFeatures: GeoFeature[];
  groundStations: GroundStation[];
  accessWindowPairs: AccessWindowPair[];
  accessAnalysisReports: AccessAnalysisReport[];
  coverageAnalysisReports: CoverageAnalysisReport[];
  currentStrategy: 'A' | 'B' | 'C';
  params: Strategy;
  satGroups: SatelliteGroupDataType[];
  satGroupsLoading: boolean;
}

// Actions
type MissionAction =
  | { type: 'SET_STRATEGY'; payload: 'A' | 'B' | 'C' }
  | { type: 'UPDATE_PARAMS'; payload: Partial<Strategy> }
  | { type: 'TOGGLE_SAT_ACTIVE'; payload: string }
  | { type: 'TOGGLE_GS_ACTIVE'; payload: string }
  | { type: 'SET_GEO_FEATURES'; payload: GeoFeature[] }
  | { type: 'ADD_GEO_FEATURE'; payload: GeoFeature }
  | { type: 'UPDATE_GEO_FEATURE'; payload: GeoFeature }
  | { type: 'TOGGLE_GEO_FEATURE_ACTIVE'; payload: string }
  | { type: 'REMOVE_GEO_FEATURE'; payload: string }
  | { type: 'SET_SATELLITES'; payload: TreeSatItem[] }
  | { type: 'ADD_SATELLITE'; payload: TreeSatItem }
  | { type: 'UPDATE_SATELLITE'; payload: { id: string; updates: Partial<Satellite> } }
  | { type: 'UPDATE_SATELLITES'; payload: Record<string, Partial<Satellite>> }
  | { type: 'APPLY_ORBIT_EVENT'; payload: OrbitEventPayload }
  | { type: 'RESET_ORBIT_DATA'; payload: { satelliteIds: string[]; groupIds: string[] } }
  | { type: 'ADD_GROUND_STATION'; payload: GroundStation }
  | { type: 'SET_GROUND_STATIONS'; payload: GroundStation[] }
  | { type: 'UPDATE_GROUND_STATION'; payload: { id: string; updates: Partial<GroundStation> } }
  | { type: 'SET_ACCESS_WINDOW_PAIRS'; payload: AccessWindowPair[] }
  | { type: 'SET_ACCESS_ANALYSIS_REPORTS'; payload: AccessAnalysisReport[] }
  | { type: 'UPSERT_ACCESS_ANALYSIS_REPORT'; payload: AccessAnalysisReport }
  | { type: 'SET_COVERAGE_ANALYSIS_REPORTS'; payload: CoverageAnalysisReport[] }
  | { type: 'UPSERT_COVERAGE_ANALYSIS_REPORT'; payload: CoverageAnalysisReport }
  | { type: 'TOGGLE_SAT_GROUP_EXPAND'; payload: string }
  | { type: 'SET_SAT_GROUPS'; payload: SatelliteGroupDataType[] };

const initialState: MissionState = {
  satellites: [],
  orbitStatusByGroup: {},
  orbitStatusBySatellite: {},
  satelliteUpdatesById: {},
  geoFeatures: [],
  groundStations: [],
  accessWindowPairs: [],
  accessAnalysisReports: [],
  coverageAnalysisReports: [],
  currentStrategy: 'B',
  params: STRATEGIES.B,
  satGroups: [],
  satGroupsLoading: false,
};

function applyStoredOrbitStatuses(
  items: TreeSatItem[],
  groupStatuses: Record<string, OrbitStatusMeta>,
  satelliteStatuses: Record<string, OrbitStatusMeta>,
): TreeSatItem[] {
  return items.map(item => {
    if ((item as SatGroup).type === 'group') {
      const grp = item as SatGroup;
      return {
        ...grp,
        orbitPointStatus: groupStatuses[grp.id] ?? grp.orbitPointStatus,
        children: grp.children.map(child => ({
          ...child,
          orbitPointStatus: satelliteStatuses[child.id] ?? child.orbitPointStatus,
        })),
      };
    }

    const sat = item as Satellite;
    return {
      ...sat,
      orbitPointStatus: satelliteStatuses[sat.id] ?? (sat.simulationGroupId ? groupStatuses[sat.simulationGroupId] : undefined) ?? sat.orbitPointStatus,
    };
  });
}

function applySatelliteUpdates(
  items: TreeSatItem[],
  updatesById: Record<string, Partial<Satellite>>,
): TreeSatItem[] {
  if (Object.keys(updatesById).length === 0) return items;
  return items.map(item => {
    if ((item as SatGroup).type === 'group') {
      const grp = item as SatGroup;
      return {
        ...grp,
        children: grp.children.map(child =>
          updatesById[child.id] ? { ...child, ...updatesById[child.id] } : child
        ),
      };
    }
    const sat = item as Satellite;
    return updatesById[sat.id] ? { ...sat, ...updatesById[sat.id] } : sat;
  });
}

function statusRank(status: OrbitStatusMeta['status']) {
  if (status === 'completed') return 4;
  if (status === 'failed') return 3;
  if (status === 'in_progress') return 2;
  return 1;
}

function statusTime(status?: OrbitStatusMeta) {
  if (!status?.updatedAt) return 0;
  const time = new Date(status.updatedAt).getTime();
  return Number.isFinite(time) ? time : 0;
}

function normalizeProgress(status: OrbitStatusMeta): OrbitStatusMeta {
  if (status.status === 'completed' && status.satellitesTotal != null) {
    return { ...status, satellitesDone: status.satellitesTotal };
  }
  if (status.satellitesTotal != null && status.satellitesDone != null) {
    return {
      ...status,
      satellitesDone: Math.max(0, Math.min(status.satellitesDone, status.satellitesTotal)),
    };
  }
  return status;
}

function mergeOrbitStatus(
  current: OrbitStatusMeta | undefined,
  incoming: OrbitStatusMeta | undefined,
): OrbitStatusMeta | undefined {
  if (!current) return incoming ? normalizeProgress(incoming) : undefined;
  if (!incoming) return normalizeProgress(current);

  const a = normalizeProgress(current);
  const b = normalizeProgress(incoming);
  const aTime = statusTime(a);
  const bTime = statusTime(b);

  if (aTime || bTime) {
    if (bTime > aTime) return b;
    if (aTime > bTime) return a;
  }

  if (statusRank(b.status) > statusRank(a.status)) return b;
  if (statusRank(a.status) > statusRank(b.status)) return a;

  return {
    ...a,
    ...b,
    satellitesTotal: Math.max(a.satellitesTotal ?? 0, b.satellitesTotal ?? 0) || a.satellitesTotal || b.satellitesTotal,
    satellitesDone: Math.max(a.satellitesDone ?? 0, b.satellitesDone ?? 0) || a.satellitesDone || b.satellitesDone,
  };
}

function mergeOrbitStatusMaps(
  current: Record<string, OrbitStatusMeta>,
  incoming: Record<string, OrbitStatusMeta>,
  preferIncomingSnapshot = false,
) {
  const merged = { ...current };
  Object.entries(incoming).forEach(([id, status]) => {
    const next = preferIncomingSnapshot && !status.updatedAt
      ? normalizeProgress(status)
      : mergeOrbitStatus(merged[id], status);
    if (next) merged[id] = next;
  });
  return merged;
}

function collectOrbitStatusMaps(items: TreeSatItem[]) {
  const groupStatuses: Record<string, OrbitStatusMeta> = {};
  const satelliteStatuses: Record<string, OrbitStatusMeta> = {};

  items.forEach(item => {
    if ((item as SatGroup).type === 'group') {
      const grp = item as SatGroup;
      if (grp.orbitPointStatus) groupStatuses[grp.id] = normalizeProgress(grp.orbitPointStatus);
      grp.children.forEach(child => {
        if (child.orbitPointStatus) satelliteStatuses[child.id] = normalizeProgress(child.orbitPointStatus);
      });
      return;
    }

    const sat = item as Satellite;
    if (sat.orbitPointStatus) {
      satelliteStatuses[sat.id] = normalizeProgress(sat.orbitPointStatus);
      if (sat.simulationGroupId) groupStatuses[sat.simulationGroupId] = normalizeProgress(sat.orbitPointStatus);
    }
  });

  return { groupStatuses, satelliteStatuses };
}

function missionReducer(state: MissionState, action: MissionAction): MissionState {
  switch (action.type) {
    case 'SET_STRATEGY':
      return { ...state, currentStrategy: action.payload, params: STRATEGIES[action.payload] };

    case 'UPDATE_PARAMS':
      return { ...state, params: { ...state.params, ...action.payload } };

    case 'TOGGLE_SAT_ACTIVE': {
      const newSatellites = state.satellites.map((item) => {
        if ('children' in item && item.id === action.payload) {
          const nextActive = !item.active;
          return {
            ...item,
            active: nextActive,
            children: item.children.map(child => ({ ...child, active: nextActive })),
          };
        }
        if ('children' in item) {
          return {
            ...item,
            children: item.children.map((child) =>
              child.id === action.payload ? { ...child, active: !child.active } : child
            ),
          };
        }
        return item.id === action.payload ? { ...item, active: !item.active } : item;
      });
      return {
        ...state,
        satellites: newSatellites,
        satGroups: treeItemsToSatGroups(newSatellites),
      };
    }

    case 'TOGGLE_SAT_GROUP_EXPAND':
      return {
        ...state,
        satellites: state.satellites.map((item) =>
          'children' in item && item.id === action.payload
            ? { ...item, expanded: !item.expanded }
            : item
        ),
      };

    case 'SET_SAT_GROUPS':
      return { ...state, satGroups: action.payload, satGroupsLoading: false };

    case 'TOGGLE_GS_ACTIVE':
      return {
        ...state,
        groundStations: state.groundStations.map((gs) =>
          gs.id === action.payload ? { ...gs, active: !gs.active } : gs
        ),
      };

    case 'SET_GEO_FEATURES':
      return { ...state, geoFeatures: action.payload };

    case 'ADD_GEO_FEATURE':
      return { ...state, geoFeatures: [...state.geoFeatures, action.payload] };

    case 'UPDATE_GEO_FEATURE':
      return {
        ...state,
        geoFeatures: state.geoFeatures.map((gf) =>
          gf.id === action.payload.id ? action.payload : gf
        ),
      };

    case 'TOGGLE_GEO_FEATURE_ACTIVE':
      return {
        ...state,
        geoFeatures: state.geoFeatures.map((gf) =>
          gf.id === action.payload ? { ...gf, active: !gf.active } : gf
        ),
      };

    case 'REMOVE_GEO_FEATURE':
      return {
        ...state,
        geoFeatures: state.geoFeatures.filter((gf) => gf.id !== action.payload),
      };

    case 'SET_SATELLITES': {
      // Build a map of existing active/expanded states by satellite/group ID
      const existingStates = new Map<string, { active?: boolean; expanded?: boolean }>();
      state.satellites.forEach(item => {
        if ('children' in item) {
          existingStates.set(item.id, { active: item.active, expanded: item.expanded });
          item.children.forEach(child => {
            existingStates.set(child.id, { active: child.active });
          });
        } else {
          existingStates.set(item.id, { active: item.active });
        }
      });

      // Map incoming satellites/groups, preserving their active/expanded states if they existed previously
      const preservedPayload = action.payload.map(item => {
        if ('children' in item) {
          const stateForGroup = existingStates.get(item.id);
          const nextChildren = item.children.map(child => {
            const stateForChild = existingStates.get(child.id);
            return {
              ...child,
              active: stateForChild?.active !== undefined ? stateForChild.active : child.active
            };
          });
          return {
            ...item,
            active: stateForGroup?.active !== undefined ? stateForGroup.active : item.active,
            expanded: stateForGroup?.expanded !== undefined ? stateForGroup.expanded : item.expanded,
            children: nextChildren
          };
        } else {
          const stateForSat = existingStates.get(item.id);
          return {
            ...item,
            active: stateForSat?.active !== undefined ? stateForSat.active : item.active
          };
        }
      });

      const { groupStatuses, satelliteStatuses } = collectOrbitStatusMaps(preservedPayload);
      const orbitStatusByGroup = mergeOrbitStatusMaps(state.orbitStatusByGroup, groupStatuses, true);
      const orbitStatusBySatellite = mergeOrbitStatusMaps(state.orbitStatusBySatellite, satelliteStatuses, true);
      const statusMergedSatellites = applyStoredOrbitStatuses(
        preservedPayload,
        orbitStatusByGroup,
        orbitStatusBySatellite,
      );
      const satellites = applySatelliteUpdates(statusMergedSatellites, state.satelliteUpdatesById);
      return {
        ...state,
        orbitStatusByGroup,
        orbitStatusBySatellite,
        satellites,
        satGroups: treeItemsToSatGroups(satellites),
      };
    }

    case 'ADD_SATELLITE': {
      const newSatellites = [...state.satellites, action.payload];
      return {
        ...state,
        satellites: newSatellites,
        satGroups: treeItemsToSatGroups(newSatellites),
      };
    }

    case 'UPDATE_SATELLITE': {
      const { id, updates } = action.payload;
      const newSatellites = state.satellites.map(item => {
        if ((item as SatGroup).type === 'group') {
          const grp = item as SatGroup;
          if (grp.id === id) {
            return {
              ...grp,
              ...updates,
              children: grp.children.map(child => ({
                ...child,
                ...updates,
              })),
            };
          }
          return {
            ...grp,
            children: grp.children.map(child =>
              child.id === id ? { ...child, ...updates } : child
            ),
          };
        }
        return (item as Satellite).id === id ? { ...item, ...updates } : item;
      });
      return {
        ...state,
        satellites: newSatellites,
        satGroups: treeItemsToSatGroups(newSatellites),
      };
    }

    case 'UPDATE_SATELLITES': {
      const updatesById = { ...state.satelliteUpdatesById, ...action.payload };
      const newSatellites = applySatelliteUpdates(state.satellites, action.payload);
      return {
        ...state,
        satelliteUpdatesById: updatesById,
        satellites: newSatellites,
        satGroups: treeItemsToSatGroups(newSatellites),
      };
    }

    case 'RESET_ORBIT_DATA': {
      const { satelliteIds, groupIds } = action.payload;

      const satelliteUpdatesById = { ...state.satelliteUpdatesById };
      satelliteIds.forEach(id => {
        delete satelliteUpdatesById[id];
      });

      const orbitStatusBySatellite = { ...state.orbitStatusBySatellite };
      satelliteIds.forEach(id => {
        delete orbitStatusBySatellite[id];
      });

      const orbitStatusByGroup = { ...state.orbitStatusByGroup };
      groupIds.forEach(id => {
        delete orbitStatusByGroup[id];
      });

      const newSatellites = state.satellites.map(item => {
        if ((item as SatGroup).type === 'group') {
          const grp = item as SatGroup;
          const isGroupTarget = groupIds.includes(grp.id);
          const nextChildren = grp.children.map(child => {
            if (satelliteIds.includes(child.id) || isGroupTarget) {
              return {
                ...child,
                backendOrbitTrack: undefined,
                ephemeris: undefined,
                propagationStatus: 'preview' as const,
                orbitPointStatus: undefined,
              };
            }
            return child;
          });
          return {
            ...grp,
            orbitPointStatus: isGroupTarget ? undefined : grp.orbitPointStatus,
            children: nextChildren,
          };
        }

        const sat = item as Satellite;
        if (satelliteIds.includes(sat.id)) {
          return {
            ...sat,
            backendOrbitTrack: undefined,
            ephemeris: undefined,
            propagationStatus: 'preview' as const,
            orbitPointStatus: undefined,
          };
        }
        return sat;
      });

      return {
        ...state,
        satelliteUpdatesById,
        orbitStatusBySatellite,
        orbitStatusByGroup,
        satellites: newSatellites,
        satGroups: treeItemsToSatGroups(newSatellites),
      };
    }

    case 'APPLY_ORBIT_EVENT': {
      const event = action.payload;
      const orbitStatusByGroup = event.subType === 'group' && event.groupId
        ? { ...state.orbitStatusByGroup, [event.groupId]: mergeOrbitStatus(state.orbitStatusByGroup[event.groupId], event)! }
        : state.orbitStatusByGroup;
      const orbitStatusBySatellite = event.subType === 'satellite' && event.satelliteId
        ? { ...state.orbitStatusBySatellite, [event.satelliteId]: mergeOrbitStatus(state.orbitStatusBySatellite[event.satelliteId], event)! }
        : state.orbitStatusBySatellite;
      const newSatellites = state.satellites.map(item => {
        if ((item as SatGroup).type === 'group') {
          const grp = item as SatGroup;
          if (event.subType === 'group' && grp.id === event.groupId) {
            return {
              ...grp,
              orbitPointStatus: {
                ...grp.orbitPointStatus,
                ...event,
              },
            };
          }
          return {
            ...grp,
            children: grp.children.map(child => {
              if (event.subType !== 'satellite' || child.id !== event.satelliteId) return child;
              return {
                ...child,
                orbitPointStatus: {
                  ...child.orbitPointStatus,
                  ...event,
                },
                propagationStatus: event.status === 'completed'
                  ? 'ready'
                  : event.status === 'failed'
                    ? 'failed'
                    : event.status === 'in_progress'
                      ? 'propagating'
                      : child.propagationStatus,
              };
            }),
          };
        }

        const sat = item as Satellite;
        const matchesSatellite = event.subType === 'satellite' && sat.id === event.satelliteId;
        const matchesSingleGroup = event.subType === 'group' && sat.simulationGroupId === event.groupId;
        if (!matchesSatellite && !matchesSingleGroup) return item;
        return {
          ...sat,
          orbitPointStatus: {
            ...sat.orbitPointStatus,
            ...event,
          },
          propagationStatus: event.status === 'completed'
            ? 'ready'
            : event.status === 'failed'
              ? 'failed'
              : event.status === 'in_progress'
                ? 'propagating'
                : sat.propagationStatus,
        };
      });
      return {
        ...state,
        orbitStatusByGroup,
        orbitStatusBySatellite,
        satellites: newSatellites,
        satGroups: treeItemsToSatGroups(newSatellites),
      };
    }

    case 'SET_GROUND_STATIONS':
      return { ...state, groundStations: action.payload };

    case 'ADD_GROUND_STATION':
      return { ...state, groundStations: [...state.groundStations, action.payload] };

    case 'UPDATE_GROUND_STATION':
      return {
        ...state,
        groundStations: state.groundStations.map(gs =>
          gs.id === action.payload.id ? { ...gs, ...action.payload.updates } : gs
        ),
      };

    case 'SET_ACCESS_WINDOW_PAIRS':
      return { ...state, accessWindowPairs: action.payload };

    case 'SET_ACCESS_ANALYSIS_REPORTS':
      return { ...state, accessAnalysisReports: action.payload };

    case 'UPSERT_ACCESS_ANALYSIS_REPORT': {
      const exists = state.accessAnalysisReports.some(report => report.reportId === action.payload.reportId);
      const nextReports = exists
        ? state.accessAnalysisReports.map(report =>
          report.reportId === action.payload.reportId
            ? {
              ...report,
              ...action.payload,
              name: action.payload.name || report.name,
              createdAt: action.payload.createdAt ?? report.createdAt,
            }
            : report
        )
        : [action.payload, ...state.accessAnalysisReports];
      return { ...state, accessAnalysisReports: nextReports };
    }

    case 'SET_COVERAGE_ANALYSIS_REPORTS':
      return { ...state, coverageAnalysisReports: action.payload };

    case 'UPSERT_COVERAGE_ANALYSIS_REPORT': {
      const exists = state.coverageAnalysisReports.some(report => report.reportId === action.payload.reportId);
      const nextReports = exists
        ? state.coverageAnalysisReports.map(report =>
          report.reportId === action.payload.reportId
            ? {
              ...report,
              ...action.payload,
              name: action.payload.name || report.name,
              createdAt: action.payload.createdAt ?? report.createdAt,
            }
            : report
        )
        : [action.payload, ...state.coverageAnalysisReports];
      return { ...state, coverageAnalysisReports: nextReports };
    }

    default:
      return state;
  }
}

// Context
interface MissionContextValue {
  state: MissionState;
  setStrategy: (letter: 'A' | 'B' | 'C') => void;
  updateParams: (partial: Partial<Strategy>) => void;
  toggleSatActive: (id: string) => void;
  toggleGSActive: (id: string) => void;
  toggleSatGroupExpand: (id: string) => void;
  setGeoFeatures: (list: GeoFeature[]) => void;
  addGeoFeature: (gf: GeoFeature) => void;
  updateGeoFeature: (gf: GeoFeature) => void;
  toggleGeoFeatureActive: (id: string) => void;
  removeGeoFeature: (id: string) => void;
  setSatellites: (list: TreeSatItem[]) => void;
  addSatellite: (sat: TreeSatItem) => void;
  updateSatellite: (id: string, updates: Partial<Satellite>) => void;
  updateSatellites: (updatesById: Record<string, Partial<Satellite>>) => void;
  applyOrbitEvent: (event: OrbitEventPayload) => void;
  resetOrbitData: (satelliteIds: string[], groupIds: string[]) => void;
  setGroundStations: (list: GroundStation[]) => void;
  addGroundStation: (gs: GroundStation) => void;
  updateGroundStation: (id: string, updates: Partial<GroundStation>) => void;
  setAccessWindowPairs: (list: AccessWindowPair[]) => void;
  setAccessAnalysisReports: (list: AccessAnalysisReport[]) => void;
  upsertAccessAnalysisReport: (report: AccessAnalysisReport) => void;
  setCoverageAnalysisReports: (list: CoverageAnalysisReport[]) => void;
  upsertCoverageAnalysisReport: (report: CoverageAnalysisReport) => void;
  satGroups: SatelliteGroupDataType[];
}

const MissionContext = createContext<MissionContextValue | null>(null);

export const MissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(missionReducer, initialState);

  const value: MissionContextValue = {
    state,
    setStrategy: (letter) => dispatch({ type: 'SET_STRATEGY', payload: letter }),
    updateParams: (partial) => dispatch({ type: 'UPDATE_PARAMS', payload: partial }),
    toggleSatActive: (id) => dispatch({ type: 'TOGGLE_SAT_ACTIVE', payload: id }),
    toggleGSActive: (id) => dispatch({ type: 'TOGGLE_GS_ACTIVE', payload: id }),
    toggleSatGroupExpand: (id) => dispatch({ type: 'TOGGLE_SAT_GROUP_EXPAND', payload: id }),
    setGeoFeatures: (list) => dispatch({ type: 'SET_GEO_FEATURES', payload: list }),
    addGeoFeature: (gf) => dispatch({ type: 'ADD_GEO_FEATURE', payload: gf }),
    updateGeoFeature: (gf) => dispatch({ type: 'UPDATE_GEO_FEATURE', payload: gf }),
    toggleGeoFeatureActive: (id) => dispatch({ type: 'TOGGLE_GEO_FEATURE_ACTIVE', payload: id }),
    removeGeoFeature: (id) => dispatch({ type: 'REMOVE_GEO_FEATURE', payload: id }),
    setSatellites: (list) => dispatch({ type: 'SET_SATELLITES', payload: list }),
    addSatellite: (sat) => dispatch({ type: 'ADD_SATELLITE', payload: sat }),
    updateSatellite: (id, updates) => dispatch({ type: 'UPDATE_SATELLITE', payload: { id, updates } }),
    updateSatellites: (updatesById) => dispatch({ type: 'UPDATE_SATELLITES', payload: updatesById }),
    applyOrbitEvent: (event) => dispatch({ type: 'APPLY_ORBIT_EVENT', payload: event }),
    resetOrbitData: (satelliteIds, groupIds) => dispatch({ type: 'RESET_ORBIT_DATA', payload: { satelliteIds, groupIds } }),
    setGroundStations: (list) => dispatch({ type: 'SET_GROUND_STATIONS', payload: list }),
    addGroundStation: (gs) => dispatch({ type: 'ADD_GROUND_STATION', payload: gs }),
    updateGroundStation: (id, updates) => dispatch({ type: 'UPDATE_GROUND_STATION', payload: { id, updates } }),
    setAccessWindowPairs: (list) => dispatch({ type: 'SET_ACCESS_WINDOW_PAIRS', payload: list }),
    setAccessAnalysisReports: (list) => dispatch({ type: 'SET_ACCESS_ANALYSIS_REPORTS', payload: list }),
    upsertAccessAnalysisReport: (report) => dispatch({ type: 'UPSERT_ACCESS_ANALYSIS_REPORT', payload: report }),
    setCoverageAnalysisReports: (list) => dispatch({ type: 'SET_COVERAGE_ANALYSIS_REPORTS', payload: list }),
    upsertCoverageAnalysisReport: (report) => dispatch({ type: 'UPSERT_COVERAGE_ANALYSIS_REPORT', payload: report }),
    satGroups: state.satGroups,
  };

  return <MissionContext.Provider value={value}>{children}</MissionContext.Provider>;
};

export function useMission(): MissionContextValue {
  const ctx = useContext(MissionContext);
  if (!ctx) throw new Error('useMission must be used inside MissionProvider');
  return ctx;
}

export default MissionContext;
